import { useEffect, useState } from "react";
import { useStore } from "../../../../store/store";
import { AccordionRoutesEnum } from "../../../../types/AccordionRoutes";
import Accordion from "../../components/Accordion";
import Button from "../../components/Button";
import { ButtonBackgroundColorType } from "../../components/Button/Button";
import { Container, Paragraph } from "./OccupationStep.style";
import { getCookie } from "../../../../utils/cookieService";
import { TrackingFlowTypeEnum } from "../../../../types/TrackingFlowTypeEnum";
import Analytics from "../../../../utils/analytics";

type OccupationType = "Employee" | "Unemployed" | "SelfEmployed" | "Student";

interface Props {
  step2: AccordionRoutesEnum;
}

export default function OccupationStep({step2}: Props) {
  const setAccordionRoute = useStore.useSetAccordionRoute();
  const setCompletedAccordionRoute = useStore.useSetCompletedAccordionRoute();
  const payload = useStore.usePayload();
  const setPayload = useStore.useSetPayload();
  const translations = useStore.useTranslations();
  const useResetPayload = useStore.useResetPayload();
  const [source, setSource] = useState<string>();

  //payload is reset when a new occupation is selected and therefore we need to fetch the source again
  useEffect(() => { 
    const form = document.querySelector(`[data-component='form-enrollment']`) as HTMLElement;
    const source = form?.dataset.source ?? "";
    setSource(source);

  }, [source])

  //Bloomreach cookie
  const exponeaCookie = () => {
    const cookie = getCookie("__exponea_etc__");
    if (cookie) {
      setPayload({ cookie: cookie });
    }
  }
  
  //Preselected products if there is preselected union in translations
  const preselectProducts = (selectedOccupation: OccupationType) => {
    exponeaCookie();

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const urlproduct = urlParams.get("produkt");
    if(!urlproduct){
      const product = translations?.general.preselectUnion

      if (selectedOccupation === "Employee") {
          setPayload({
            wantsUnemploymentBenefits: true,
            wantsSalaryInsuranceAndUnion: true,
            wantsSalaryInsurance: false,
            wantsHRJura: false,
            wantsBasic: false,
            wantsUnion: (product && product === "Union") ? true : false,
          });
      } else if (selectedOccupation === "Unemployed") {
          setPayload({
            wantsUnemploymentBenefits: true,
            wantsSalaryInsurance: false,
            wantsUnion: (product && product === "Union") ? true : false,
            wantsHRJura: false,
            wantsBasic: (product && product === "union") ? true : false,
          });
      } else if (selectedOccupation === "SelfEmployed") {
          setPayload({
            wantsUnemploymentBenefits: true,
            wantsUnion: false,
            wantsSalaryInsurance: false,
            wantsHRJura: true,
            wantsBasic: true,
          });
      } else if (selectedOccupation === "Student") {
          setPayload({
            wantsUnemploymentBenefits: true,
            wantsUnion: true,
            wantsSalaryInsurance: false,
            wantsHRJura: false,
            wantsBasic: false,
          }); 
      }
    }

    
  };

  const goToNextStep = (selectedOccupation: OccupationType) => {
    preselectProducts(selectedOccupation);
    setCompletedAccordionRoute(AccordionRoutesEnum.OccupationStep);
    setAccordionRoute(step2, payload, step2 === AccordionRoutesEnum.ProductStepV2 ? TrackingFlowTypeEnum.VerticalV2 : TrackingFlowTypeEnum.Vertical);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <Accordion
      title="1. Din arbejdssituation"
      step={AccordionRoutesEnum.OccupationStep}
      onNext={() => {
        //Add if autoclick is rolled back
        //preselectProducts();
        setCompletedAccordionRoute(AccordionRoutesEnum.OccupationStep);
        setAccordionRoute(step2, payload, step2 === AccordionRoutesEnum.ProductStepV2 ? TrackingFlowTypeEnum.VerticalV2 : TrackingFlowTypeEnum.Vertical);
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      }}
      stepComplete={payload.occupation ? true : false}
      headerImg="/assets/images/outline_city.svg"
      step2={step2}
    >
    
      <Paragraph>{translations?.occupationStep.description}</Paragraph>
      <Container>
        <Button 
          bgColor={ButtonBackgroundColorType.Blue} 
          blockButton={true} 
          isChecked={payload.occupation === "Employee"} 
          onClick={() =>  {
            const selectedOccupation: OccupationType = "Employee";
            useResetPayload(); 
            setPayload({ source: source});
            setPayload({ occupation: selectedOccupation });
            exponeaCookie();
            //new for auto click on next when occupation is selected
            goToNextStep(selectedOccupation);
          }}
        >
          {translations?.occupationStep.employed}
        </Button>
        <Button 
          bgColor={ButtonBackgroundColorType.Blue} 
          blockButton={true} 
          isChecked={payload.occupation === "Unemployed"} 
          onClick={() => { 
            const selectedOccupation: OccupationType = "Unemployed";
            useResetPayload(); 
            setPayload({ source: source});
            setPayload({ occupation: selectedOccupation });
            exponeaCookie();
            //new for auto click on next when occupation is selected
            goToNextStep(selectedOccupation);
          }}
        >
          {translations?.occupationStep.unemployed}
        </Button>
      </Container>
      <Container>
        <Button 
          bgColor={ButtonBackgroundColorType.Blue} 
          blockButton={true} 
          isChecked={payload.occupation === "SelfEmployed"} 
          onClick={() => {
            const selectedOccupation: OccupationType = "SelfEmployed";
            useResetPayload(); 
            setPayload({ source: source});
            setPayload({ occupation: selectedOccupation });
            exponeaCookie();
            //new for auto click on next when occupation is selected
            goToNextStep(selectedOccupation);
          }}
        >
          {translations?.occupationStep.selfEmployed}
        </Button>
        <Button 
          bgColor={ButtonBackgroundColorType.Blue} 
          blockButton={true} 
          isChecked={payload.occupation === "Student"} 
          onClick={() => {
            const selectedOccupation: OccupationType = "Student";
            useResetPayload(); 
            setPayload({ source: source});
            setPayload({ occupation: selectedOccupation });
            exponeaCookie();
            //new for auto click on next when occupation is selected
            goToNextStep(selectedOccupation);
          }}
        >
          {translations?.occupationStep.student}
        </Button>
      </Container>
    </Accordion>
  );
}
